import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import {getConfig} from './config'
import {getUserToken} from './token'
import {getLogger} from './logging'

const {guestPortalApiUrl} = getConfig()
const logger = getLogger('guestPortalApi')

export interface CmsPropertyImage {
  image: string
  altText?: string
}

export interface CmsPropertyCoordinates {
  latitude: number
  longitude: number
}

export interface CmsPropertyAddress {
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  countryCode?: string
  postalCode?: string
}

/**
 * A property as returned by GET /cms/properties.
 *
 * These fields mirror the GraphQL selection in
 * libs/ibe-admin-client/src/models/property.ts. The CMS returns only what that
 * query selects, so a field added here but not there resolves to `undefined`
 * at runtime instead of failing to compile. The CMS carries marketing content
 * only — it has no check-in/check-out times, timezone, or cancellation policy.
 */
export interface CmsProperty {
  id: string
  propertyApaleoId: string
  name: string
  description?: string
  modelId?: string
  images?: CmsPropertyImage[]
  coordinates?: CmsPropertyCoordinates
  location?: string
  regions?: string[]
  address?: CmsPropertyAddress
  isEnabled: boolean
  neighborhood?: string
  isPopup?: boolean
  highlightedFeatures?: string[]
  features?: string[]
}

/** CMS properties keyed by Apaleo property id. */
export type CmsPropertyMap = Record<string, CmsProperty>

export const guestPortalApi = createApi({
  reducerPath: 'guestPortalApi',
  baseQuery: fetchBaseQuery({
    baseUrl: guestPortalApiUrl,
    prepareHeaders: async (headers): Promise<Headers> => {
      try {
        // injects authorization token into request headers
        const token = getUserToken()
        if (token?.length) {
          headers.set('Authorization', `Bearer ${token}`)
        }
      } catch (e) {
        logger.error({
          message: 'Error occurred setting guest token on HTTP request',
          errorData: {
            error: e
          }
        })
      }
      return headers
    }
  }),
  tagTypes: [],
  endpoints: builder => ({
    cancelReservation: builder.mutation({
      query({reservationId, dealId}: {reservationId: string; dealId: string}) {
        return {
          url: `/reservations/${reservationId}/cancel?dealId=${dealId}`,
          method: 'PUT'
        }
      }
      // invalidatesTags: ['Reservations']
    }),
    getOTAReservations: builder.query({
      query({reservationStatus}) {
        return `/reservations/ota?reservationStatus=${reservationStatus}`
      }
    }),
    postOTAReservation: builder.mutation({
      query({confirmationCode, lastName, ota}) {
        return {
          url: `/reservations/link-ota-reservation`,
          method: 'POST',
          body: {
            confirmationCode,
            lastName,
            ota
          }
        }
      }
    }),
    getMemberReservations: builder.query({
      query({reservationStatus, reservationId, sortOrder}) {
        const params = new URLSearchParams()
        if (reservationStatus) {
          params.append('reservationStatus', reservationStatus)
        }
        if (reservationId) {
          params.append('reservationId', reservationId)
        }
        if (sortOrder) {
          params.append('sortOrder', sortOrder)
        }
        return `/reservations?${params.toString()}`
      },
      providesTags: ['Reservations']
    }),
    getMemberReservation: builder.query({
      query({reservationId}) {
        return `/reservations/${reservationId}`
      },
      providesTags: ['Reservations']
    }),
    getMemberReservationsDetailed: builder.query({
      query({reservationStatus, reservationId}) {
        const params = new URLSearchParams()
        if (reservationStatus) {
          params.append('reservationStatus', reservationStatus)
        }
        if (reservationId) {
          params.append('reservationId', reservationId)
        }
        return `/reservations-detailed?${params.toString()}`
      },
      providesTags: ['Reservations']
    }),
    getContactInfo: builder.query({
      query({}) {
        return `/contact`
      },
      providesTags: ['ContactInfo']
    }),
    postContactNotificationPreferences: builder.mutation({
      query({smsOptOut, marketingEmailUnsubscribed}) {
        return {
          url: `/contact/notifications`,
          method: 'POST',
          body: {
            smsOptOut,
            marketingEmailUnsubscribed
          }
        }
      },
      invalidatesTags: ['ContactInfo']
    }),
    getProperty: builder.query({
      query({propertyId}) {
        return `/properties/${propertyId}`
      },
      providesTags: ['Property']
    }),
    getCMSProperties: builder.query({
      query({}) {
        return `/cms/properties`
      },
      transformResponse: (response: any): CmsPropertyMap => {
        return response.data.reduce(
          (acc: CmsPropertyMap, property: CmsProperty) => {
            acc[property.propertyApaleoId] = property
            return acc
          },
          {}
        )
      },
      providesTags: []
    }),
    getPMSProperties: builder.query({
      query({}) {
        return `/pms/properties`
      },
      transformResponse: (response: any) => {
        return response.data.reduce((acc: any, property: any) => {
          acc[property.id] = property
          return acc
        }, {})
      },
      providesTags: []
    }),
    getPaymentMethods: builder.query({
      query({reservation}) {
        return `/payment-methods?propertyId=${reservation.propertyId}`
      },
      providesTags: []
    }),
    getPaymentSession: builder.query({
      query({reservation}) {
        return {
          url: `/payment-session`,
          method: 'POST',
          body: {
            reservationId: reservation.reservationId,
            bookingId: reservation.bookingId,
            propertyId: reservation.propertyId,
            returnUrl: 'http://www.example.com',
            countryCode: 'US'
          }
        }
      },
      providesTags: []
    }),
    getReservationInvoice: builder.query({
      query({reservationId}) {
        return `/reservations/${reservationId}/invoice`
      }
    })
  })
})

export const {
  useCancelReservationMutation,
  usePostOTAReservationMutation,
  useGetOTAReservationsQuery,
  useGetMemberReservationsQuery,
  useGetMemberReservationQuery,
  useGetMemberReservationsDetailedQuery,
  useGetPropertyQuery,
  useGetCMSPropertiesQuery,
  useGetPMSPropertiesQuery,
  useGetPaymentMethodsQuery,
  useGetPaymentSessionQuery,
  useGetContactInfoQuery,
  usePostContactNotificationPreferencesMutation,
  useGetReservationInvoiceQuery
} = guestPortalApi
